import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { resourceSections } from '../data/resources';
import NewsletterSignup from '../components/NewsletterSignup';

const ResourcesLandingPage = () => {
  return (
    <div className="bg-cream pt-24 min-h-screen" data-testid="resources-landing-page">
      {/* Hero Section */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-terracotta font-body text-sm tracking-widest uppercase mb-4">
              Free Resources
            </p>
            <h1 className="font-heading text-5xl md:text-6xl text-forest leading-[1.1] mb-6">
              Tools for Your<br />
              <span className="italic text-terracotta">Grounded Journey</span>
            </h1>
            <p className="text-stone-600 text-lg font-body leading-relaxed">
              A collection of guides, recipes, and resources to support your path 
              back to yourself. Take what resonates, leave what doesn't.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 4 Section Cards */}
      <section className="py-8 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-8">
            {resourceSections.map((section, index) => (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
              >
                <Link
                  to={`/resources/${section.id}`}
                  data-testid={`resource-section-${section.id}`}
                  className="group block h-full"
                >
                  <div className="relative overflow-hidden rounded-2xl h-full min-h-[320px] bg-white shadow-card hover:shadow-lg transition-all duration-500">
                    {/* Background Image */}
                    <div className="absolute inset-0">
                      <img
                        src={section.image}
                        alt={section.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className={`absolute inset-0 bg-gradient-to-t ${section.color} opacity-80`} />
                    </div>

                    {/* Content */}
                    <div className="relative h-full flex flex-col justify-end p-8 text-white">
                      <p className="text-white/70 font-body text-sm tracking-widest uppercase mb-2">
                        {section.subtitle}
                      </p>
                      <h2 className={`font-heading text-3xl md:text-4xl mb-3 ${section.titleColor || 'text-white'}`}>
                        {section.title}
                      </h2>
                      <p className="text-white/80 font-body mb-6 max-w-md">
                        {section.description}
                      </p>
                      <div className="flex items-center gap-2 text-white font-body font-medium group-hover:gap-4 transition-all duration-300">
                        Explore {section.title}
                        <ArrowRight size={18} />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Signup Section */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <NewsletterSignup variant="section" source="resources-landing" />
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-stone-100">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-heading text-3xl md:text-4xl text-forest mb-4">
              Want Personalized Guidance?
            </h2>
            <p className="text-stone-600 text-lg font-body leading-relaxed mb-8">
              These resources are a starting point. For deeper, personalized work, 
              let's connect and create a path that's right for you.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-forest text-cream px-8 py-4 rounded-full font-body font-medium hover:bg-forest-dark transition-all duration-300"
            >
              Let's Talk
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ResourcesLandingPage;
